import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { ILead } from "../models/Lead";
import apiConfig from "../utils/apiConfig";

export default function useLeadMutations(
  leadId: string,
  componentName = "useLeadMutations"
) {
  const { getFile, post, put, patch, del } = useApiHelper(
    apiConfig(componentName)
  );
  const queryClient = useQueryClient();

  const update = useMutation(
    (lead: ILead) => put(`/lead/${leadId}`, lead),
    {
      onMutate: async (lead: ILead) => {
        await queryClient.cancelQueries({ queryKey: ["leads"] });
        const previousLead = queryClient.getQueryData(["leads", lead.id]);

        queryClient.setQueryData(["leads", lead.id], {
          success: true,
          message: "Optimistically updated",
          data: lead,
        });

        return { previousLead, lead };
      },
      onError: (err, lead, context) => {
        queryClient.setQueryData(
          ["leads", context?.lead.id],
          context?.previousLead
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(["lead", leadId]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["unscheduledLeads"]);
        queryClient.invalidateQueries(["scheduledLeads"]);
        // ApplyVAT writes through to the vehicle, so vehicle views must refetch.
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const remove = useMutation(
    (id: string) => del(`/lead/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lead", leadId]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["unscheduledLeads"]);
        queryClient.invalidateQueries(["scheduledLeads"]);
        queryClient.invalidateQueries(["trips"]);
        queryClient.invalidateQueries(["trucks"]);
      },
    }
  );

  const complete = useMutation(
    (id: string) => post(`/lead/${id}/complete`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lead", leadId]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const downloadDoc = useMutation(
    (ids: any) => getFile(`/lead/${ids.leadId}/documents/${ids.docId}`),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  const getCollectionNote = useMutation(
    ({
      leadId: noteLeadId,
      createNew,
    }: {
      leadId: string;
      createNew?: boolean;
    }) =>
      getFile(
        `/lead/${noteLeadId}/documents/note${createNew ? "?createNew=true" : ""}`
      ),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  const updateNotes = useMutation(
    (data: {
      leadId: string;
      officeNotes?: string | null;
      driverNotes?: string | null;
      yardNotes?: string | null;
    }) => patch(`/note/lead`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lead", leadId]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return {
    update,
    updateNotes,
    remove,
    complete,
    downloadDoc,
    getCollectionNote,
  };
}
